import React, { useState, useEffect, useMemo } from 'react';
import supabase from '../config/supabase';
import { useVendorDirectory } from '../utils/useVendorDirectory';

const ItemDatabaseBrowser = ({ onAddItem, currentItems = [], inventoryData = [] }) => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());

  const { resolveVendor, resolveUniqueVendors } = useVendorDirectory();

  // Use passed inventory data instead of loading from database
  useEffect(() => {
    console.log('🔍 [ItemDatabaseBrowser] useEffect triggered. inventoryData:', inventoryData?.length || 0, 'items');
    console.log('🔍 [ItemDatabaseBrowser] window.globalInventoryData:', window.globalInventoryData?.length || 0, 'items');

    if (inventoryData && inventoryData.length > 0) {
      console.log('✅ [ItemDatabaseBrowser] Using passed inventory data:', inventoryData.length, 'items');
      console.log('🔍 [ItemDatabaseBrowser] Sample item:', inventoryData[0]);
      setInventoryItems(inventoryData);

      // Extract unique values for filters; use canonical vendor names
      const uniqueCategories = [...new Set(inventoryData.map(item => item.category).filter(Boolean))].sort();
      const uniqueLocations = [...new Set(inventoryData.map(item => item.location).filter(Boolean))].sort();
      const uniqueVendors = resolveUniqueVendors(inventoryData);

      console.log('🔍 [ItemDatabaseBrowser] Filter options - Categories:', uniqueCategories.length, 'Locations:', uniqueLocations.length, 'Vendors:', uniqueVendors.length);

      setCategories(uniqueCategories);
      setLocations(uniqueLocations);
      setVendors(uniqueVendors);
      setLoading(false);
      console.log('✅ [ItemDatabaseBrowser] Setup complete, loading set to false');
    } else {
      console.log('⚠️ [ItemDatabaseBrowser] No inventory data passed, loading from database...');
      loadInventoryItems();
    }
  }, [inventoryData]);

  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryItems, searchQuery, categoryFilter, locationFilter, vendorFilter, currentItems, resolveVendor]);

  // Listen for external filter changes from count.html
  useEffect(() => {
    const handleDatabaseFiltersChanged = (event) => {
      console.log('🔍 [ItemDatabaseBrowser] Received databaseFiltersChanged event:', event.detail);
      const { category, location, vendor } = event.detail;

      // Map filter values - 'all' means no filter (empty string)
      const mappedCategory = category === 'all' ? '' : category;
      const mappedLocation = location === 'all' ? '' : location;
      const mappedVendor = vendor === 'all' ? '' : vendor;

      // Update filter states
      if (category !== undefined) setCategoryFilter(mappedCategory);
      if (location !== undefined) setLocationFilter(mappedLocation);
      if (vendor !== undefined) setVendorFilter(mappedVendor);

      console.log('🔍 [ItemDatabaseBrowser] Applied filters:', {
        category: mappedCategory,
        location: mappedLocation,
        vendor: mappedVendor
      });
    };

    // Add event listener
    document.addEventListener('databaseFiltersChanged', handleDatabaseFiltersChanged);

    // Cleanup
    return () => {
      document.removeEventListener('databaseFiltersChanged', handleDatabaseFiltersChanged);
    };
  }, []);

  const loadInventoryItems = async () => {
    setLoading(true);
    try {
      // Get store_id from localStorage to scope query
      const storedStoreId = localStorage.getItem('selectedStoreId');
      const storeId = storedStoreId ? parseInt(storedStoreId) : null;
      if (!storeId) {
        console.warn('⚠️ [ItemDatabaseBrowser] No store_id found, cannot load items');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('store_id', storeId)
        // Skip soft-deleted/archived items so deleted items don't reappear in the picker.
        .not('is_active', 'is', false)
        .order('description');

      if (error) {
        console.error('Error loading inventory items:', error);
        return;
      }

      setInventoryItems(data || []);

      // Extract unique values for filters; use canonical vendor names
      const uniqueCategories = [...new Set(data.map(item => item.category).filter(Boolean))].sort();
      const uniqueLocations = [...new Set(data.map(item => item.location).filter(Boolean))].sort();
      const uniqueVendors = resolveUniqueVendors(data || []);

      setCategories(uniqueCategories);
      setLocations(uniqueLocations);
      setVendors(uniqueVendors);

    } catch (error) {
      console.error('Error loading inventory items:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = inventoryItems;

    console.log('🔍 [ItemDatabaseBrowser] Starting applyFilters with:', {
      totalItems: inventoryItems.length,
      searchQuery,
      categoryFilter,
      locationFilter,
      vendorFilter
    });

    // Filter out items already in current items list
    const currentItemIds = currentItems.map(item => item.id);
    filtered = filtered.filter(item => !currentItemIds.includes(item.id));
    console.log('🔍 [ItemDatabaseBrowser] After excluding current items:', filtered.length);

    // Apply search query – search against canonical vendor name so aliases resolve
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        (item.description || '').toLowerCase().includes(query) ||
        (item.brand || '').toLowerCase().includes(query) ||
        (resolveVendor(item.vendor) || item.vendor || '').toLowerCase().includes(query) ||
        (item.category || '').toLowerCase().includes(query) ||
        (item.location || '').toLowerCase().includes(query) ||
        (item.supc || '').toString().includes(query)
      );
      console.log('🔍 [ItemDatabaseBrowser] After search filter:', filtered.length);
    }

    // Apply category filter - exact match
    if (categoryFilter) {
      console.log('🔍 [ItemDatabaseBrowser] Applying category filter:', categoryFilter);
      console.log('🔍 [ItemDatabaseBrowser] Available categories in data:', [...new Set(inventoryItems.map(item => item.category).filter(Boolean))]);

      // Exact match for category names
      filtered = filtered.filter(item => item.category === categoryFilter);
      console.log('🔍 [ItemDatabaseBrowser] After category filter:', filtered.length);
    }

    // Apply location filter - exact match
    if (locationFilter) {
      console.log('🔍 [ItemDatabaseBrowser] Applying location filter:', locationFilter);
      console.log('🔍 [ItemDatabaseBrowser] Available locations in data:', [...new Set(inventoryItems.map(item => item.location).filter(Boolean))]);

      // Exact match for location names
      filtered = filtered.filter(item => item.location === locationFilter);
      console.log('🔍 [ItemDatabaseBrowser] After location filter:', filtered.length);
    }

    // Apply vendor filter - match by canonical resolved name
    if (vendorFilter) {
      console.log('🔍 [ItemDatabaseBrowser] Applying vendor filter:', vendorFilter);
      // Compare resolved canonical names so aliases map to the same bucket
      filtered = filtered.filter(item => resolveVendor(item.vendor) === vendorFilter);
      console.log('🔍 [ItemDatabaseBrowser] After vendor filter:', filtered.length);
    }

    console.log('🔍 [ItemDatabaseBrowser] Final filtered items:', filtered.length);
    setFilteredItems(filtered);
  };

  const handleAddItem = (item) => {
    console.log('🧩 [ItemDatabaseBrowser] handleAddItem called with item:', item);
    console.log('🧩 [ItemDatabaseBrowser] onAddItem callback type:', typeof onAddItem);

    if (onAddItem && typeof onAddItem === 'function') {
      console.log('🧩 [ItemDatabaseBrowser] Calling onAddItem callback...');
      try {
        onAddItem(item);
        console.log('✅ [ItemDatabaseBrowser] onAddItem callback completed successfully');
      } catch (error) {
        console.error('❌ [ItemDatabaseBrowser] Error in onAddItem callback:', error);
      }
    } else {
      console.error('❌ [ItemDatabaseBrowser] onAddItem callback not available or not a function');
      // Try to call global function as fallback
      if (typeof window.addItemToCountList === 'function') {
        console.log('🔧 [ItemDatabaseBrowser] Using global fallback function');
        window.addItemToCountList(item);
      } else {
        console.error('❌ [ItemDatabaseBrowser] No global addItemToCountList function available either');
      }
    }
  };

  const handleDragStart = (e, item) => {
    e.dataTransfer.setData('application/inventory-item', JSON.stringify(item));
    e.dataTransfer.effectAllowed = 'copy';
    console.log('🟡 [ItemDatabaseBrowser] Drag started for:', item.description);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
    setLocationFilter('');
    setVendorFilter('');
  };

  // Helper function to get the display price (unit > split > case)
  const getDisplayPrice = (item) => {
    if (item.unit_price && item.unit_price > 0) {
      return { price: item.unit_price, type: 'unit' };
    }
    if (item.split_price && item.split_price > 0) {
      return { price: item.split_price, type: 'split' };
    }
    if (item.case_price && item.case_price > 0) {
      return { price: item.case_price, type: 'case' };
    }
    return { price: 0, type: 'unit' };
  };

  // Toggle card expansion
  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2 text-muted">Loading inventory...</span>
      </div>
    );
  }

  return (
    <div className="h-100 d-flex flex-column">
      {/* Search Input */}
      <div className="p-2 border-bottom">
        <div className="position-relative">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              fontSize: '0.75rem',
              padding: '6px 10px',
              paddingLeft: '30px',
              border: '1px solid #D4C4B0',
              borderRadius: '4px',
              fontFamily: "'Open Sans', sans-serif"
            }}
          />
          <i
            className="feather icon-search position-absolute"
            style={{
              left: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.8rem',
              color: '#6c757d',
              pointerEvents: 'none'
            }}
          ></i>
          {searchQuery && (
            <button
              type="button"
              className="btn btn-sm position-absolute"
              onClick={() => setSearchQuery('')}
              style={{
                right: '2px',
                top: '50%',
                transform: 'translateY(-50%)',
                padding: '2px 6px',
                fontSize: '0.7rem',
                border: 'none',
                background: 'transparent',
                color: '#6c757d'
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {searchQuery && (
          <small className="text-muted mt-1 d-block">
            Searching for "{searchQuery}" - {filteredItems.length} results
          </small>
        )}
      </div>






      {/* Items List */}
      <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
        {filteredItems.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-muted mb-2">
              <i className="feather icon-search" style={{ fontSize: '2rem' }}></i>
            </div>
            <h6 className="text-muted">No Items Found</h6>
            <p className="small text-muted">
              {inventoryItems.length === 0
                ? 'No inventory items in database'
                : 'Try adjusting your search or filters'
              }
            </p>
          </div>
        ) : (
          <div className="list-group list-group-flush">
            {filteredItems.map(item => {
              const isExpanded = expandedItems.has(item.id);
              const displayPrice = getDisplayPrice(item);

              return (
                <div
                  key={item.id}
                  className="mb-2 p-2"
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, item)}
                  style={{
                    fontSize: '0.85rem',
                    background: 'var(--bs-white)',
                    border: '1px solid var(--bs-border-color)',
                    borderRadius: 'var(--bs-border-radius)',
                    boxShadow: 'var(--bs-box-shadow-sm)',
                    transition: 'var(--transition)',
                    fontFamily: 'var(--bs-body-font-family)',
                    fontWeight: 'var(--bs-body-font-weight)',
                    lineHeight: 'var(--bs-body-line-height)',
                    color: 'var(--bs-body-color)',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    cursor: 'grab'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--shadow-hover)';
                    e.currentTarget.style.borderColor = 'var(--tree-accent)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--bs-box-shadow-sm)';
                    e.currentTarget.style.borderColor = 'var(--bs-border-color)';
                  }}
                >
                  {/* Two-Row Layout with Fixed Right Column */}
                  <div
                    className="d-flex align-items-start"
                    style={{ position: 'relative' }}
                  >
                    <div
                      className="flex-grow-1"
                      style={{
                        cursor: 'pointer',
                        paddingRight: '120px' // Space for price and button
                      }}
                      onClick={() => toggleExpanded(item.id)}
                    >
                      {/* First Row - Item Name (Full Width) */}
                      <div
                        className="fw-bold mb-1"
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--bs-emphasis-color)',
                          fontWeight: '600',
                          lineHeight: '1.2',
                          wordBreak: 'break-word',
                          hyphens: 'auto',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis',
                          fontFamily: 'var(--bs-body-font-family)'
                        }}
                        title={item.description || 'Unknown Item'}
                      >
                        {item.description || 'Unknown Item'}
                      </div>

                      {/* Second Row - Location with tab spacing */}
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--bs-secondary-color)',
                          paddingLeft: '1ch', // One character space for tab alignment
                          marginBottom: '2px'
                        }}
                      >
                        {/* Location */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <strong
                            style={{
                              color: 'var(--bs-emphasis-color)',
                              fontSize: '0.75rem',
                              marginRight: '6px',
                              flexShrink: 0,
                              minWidth: '25px'
                            }}
                          >
                            Loc:
                          </strong>
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '120px'
                            }}
                            title={item.location || 'No Location'}
                          >
                            {item.location || 'No Location'}
                          </span>
                        </div>
                      </div>

                      {/* Third Row - Vendor aligned with Location */}
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--bs-secondary-color)',
                          paddingLeft: '1ch' // Same tab alignment as location
                        }}
                      >
                        {/* Vendor */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <strong
                            style={{
                              color: 'var(--bs-emphasis-color)',
                              fontSize: '0.75rem',
                              marginRight: '6px',
                              flexShrink: 0,
                              minWidth: '25px' // Same width as "Loc:" for alignment
                            }}
                          >
                            V:
                          </strong>
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '120px'
                            }}
                            title={resolveVendor(item.vendor) || 'No Vendor'}
                          >
                            {resolveVendor(item.vendor) || 'No Vendor'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Fixed Right Column - Price and Button */}
                    <div
                      style={{
                        position: 'absolute',
                        right: '2px',
                        top: '0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '4px',
                        width: '115px'
                      }}
                    >
                      {/* Price with monospace alignment */}
                      <div
                        className="fw-bold"
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--bs-success)',
                          fontWeight: '600',
                          fontFamily: 'var(--bs-font-monospace)',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}
                      >
                        <span style={{ display: 'inline-block', textAlign: 'right', minWidth: '60px' }}>
                          ${displayPrice.price.toFixed(2)}
                        </span>
                        <small
                          style={{
                            color: 'var(--bs-secondary-color)',
                            fontSize: '0.7rem',
                            fontFamily: 'var(--bs-font-monospace)',
                            display: 'inline-block',
                            textAlign: 'left',
                            minWidth: '35px'
                          }}
                        >
                          /{displayPrice.type}
                        </small>
                      </div>

                      {/* Add Button - Fixed position */}
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddItem(item);
                        }}
                        style={{
                          fontSize: '0.7rem',
                          width: '50px',
                          height: '28px',
                          borderColor: 'var(--bs-primary)',
                          color: 'var(--bs-primary)',
                          fontWeight: '500',
                          padding: '2px 4px'
                        }}
                        title="Add to count list"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bs-primary)';
                          e.currentTarget.style.color = 'var(--bs-white)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--bs-primary)';
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div
                      className="mt-2 pt-2"
                      style={{
                        borderTop: '1px solid var(--bs-border-color)',
                        backgroundColor: 'var(--bs-light-bg-subtle)'
                      }}
                    >
                      <div
                        className="row g-2"
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--bs-secondary-color)'
                        }}
                      >
                        {item.brand && (
                          <div className="col-6">
                            <i
                              className="feather icon-tag me-1"
                              style={{
                                fontSize: '0.7rem',
                                color: 'var(--icon-color)'
                              }}
                            ></i>
                            <strong style={{ color: 'var(--bs-emphasis-color)' }}>Brand:</strong> {item.brand}
                          </div>
                        )}
                        {item.manufacturer_code && (
                          <div className="col-6">
                            <i
                              className="feather icon-hash me-1"
                              style={{
                                fontSize: '0.7rem',
                                color: 'var(--icon-color)'
                              }}
                            ></i>
                            <strong style={{ color: 'var(--bs-emphasis-color)' }}>Product ID:</strong> {item.manufacturer_code}
                          </div>
                        )}
                        {item.category && (
                          <div className="col-6">
                            <i
                              className="feather icon-folder me-1"
                              style={{
                                fontSize: '0.7rem',
                                color: 'var(--icon-color)'
                              }}
                            ></i>
                            <strong style={{ color: 'var(--bs-emphasis-color)' }}>Category:</strong> {item.category}
                          </div>
                        )}
                        {item.pack_size && (
                          <div className="col-6">
                            <i
                              className="feather icon-package me-1"
                              style={{
                                fontSize: '0.7rem',
                                color: 'var(--icon-color)'
                              }}
                            ></i>
                            <strong style={{ color: 'var(--bs-emphasis-color)' }}>Pack Size:</strong> {item.pack_size}
                          </div>
                        )}
                        {item.unit && (
                          <div className="col-6">
                            <i
                              className="feather icon-box me-1"
                              style={{
                                fontSize: '0.7rem',
                                color: 'var(--icon-color)'
                              }}
                            ></i>
                            <strong style={{ color: 'var(--bs-emphasis-color)' }}>Unit:</strong> {item.unit}
                          </div>
                        )}
                        <div className="col-6">
                          <i
                            className="feather icon-clock me-1"
                            style={{
                              fontSize: '0.7rem',
                              color: 'var(--icon-color)'
                            }}
                          ></i>
                          <strong style={{ color: 'var(--bs-emphasis-color)' }}>Last Updated:</strong> {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'Unknown'}
                        </div>

                        {/* Price Breakdown */}
                        <div className="col-12 mt-2">
                          <div className="d-flex gap-3" style={{ fontSize: '0.75rem' }}>
                            {item.unit_price > 0 && (
                              <span style={{ color: 'var(--bs-success)' }}>
                                <strong>Unit:</strong> ${item.unit_price.toFixed(2)}
                              </span>
                            )}
                            {item.split_price > 0 && (
                              <span style={{ color: 'var(--bs-info)' }}>
                                <strong>Split:</strong> ${item.split_price.toFixed(2)}
                              </span>
                            )}
                            {item.case_price > 0 && (
                              <span style={{ color: 'var(--bs-warning)' }}>
                                <strong>Case:</strong> ${item.case_price.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemDatabaseBrowser;
